import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { finalize, Observable, switchMap, take } from 'rxjs';
import { ContextService } from 'core/services/context.service';
import { inject, Injectable } from '@angular/core';
import { IdempotencyKeyService } from '../services/idempotency-key.service';
import { ApiRequestTrackerService } from 'app/shared/services/forms/api-request-tracker.service';

const siteIdHeader = 'X-Lazer-Tech-Site';
const clientIdHeader = 'X-Lazer-Tech-Client';
const idempotencyKeyHeader = 'Idempotency-Key';
const trackerKeyHeader = 'X-Lazer-Track-API';

@Injectable({
  providedIn: 'root',
})
export class ApiInterceptor implements HttpInterceptor {
  protected readonly contextService = inject(ContextService);
  protected readonly idempotencyKeyService = inject(IdempotencyKeyService);
  protected readonly apiRequestTracker = inject(ApiRequestTrackerService);

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const siteId = this.contextService.siteId.value ?? '';
    const clientId = this.contextService.clientId.value ?? '';
    const apiIdentifier = req.headers.get(trackerKeyHeader) || '';

    if (apiIdentifier) {
      this.apiRequestTracker.startRequest(apiIdentifier);
    }

    // If the request URL is auth based, only add siteId header
    if (req.url.includes('auth')) {
      const newReq = req.clone({
        setHeaders: {
          [siteIdHeader]: siteId,
          [clientIdHeader]: clientId,
        },
      });

      return next.handle(newReq).pipe(
        finalize(() => {
          if (apiIdentifier) {
            this.apiRequestTracker.endRequest(apiIdentifier);
          }
        })
      );
    }

    // For other requests, include both headers with idempotency key handling.
    return this.idempotencyKeyService.key$.pipe(
      take(1),
      switchMap((key) => {
        const newReq = req.clone({
          setHeaders: {
            [siteIdHeader]: siteId,
            [clientIdHeader]: clientId,
            [idempotencyKeyHeader]: key,
          },
        });

        return next.handle(newReq).pipe(
          finalize(() => {
            this.idempotencyKeyService.resetKey();
            if (apiIdentifier) {
              this.apiRequestTracker.endRequest(apiIdentifier);
            }
          })
        );
      })
    );
  }
}
